import { useState, useMemo } from 'react';
import Button from '../ui/Button';
import { useGamification } from '../../context/GamificationContext';
import { useAuditLog } from '../../context/AuditLogContext';
import { useRoles, ROLES } from '../../context/RolesContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function startOfWeek() {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth() {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

const CATEGORY_COLORS = {
  sales:       { bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-300'   },
  customer:    { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
  performance: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300' },
  special:     { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
};

const AVATAR_PALETTE = ['#2E8BF0', '#8B5CF6', '#22C55E', '#F59E0B', '#EF4444', '#00D4FF'];

// ─── Achievement badge art ────────────────────────────────────────────────────

const ACHIEVEMENT_ICON_PATHS = {
  clipboard:           'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  'check-circle':      'M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  target:              'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Zm-9 3a9 9 0 1 1 18 0 9 9 0 0 1-18 0Z',
  truck:               'M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12',
  banknotes:           'M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z',
  'hand-raised':       'M10.05 4.575a1.575 1.575 0 1 0-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 0 1 3.15 0v1.5m-3.15 0 .075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 0 1 3.15 0V15M6.9 7.575a1.575 1.575 0 1 0-3.15 0v8.175a6.75 6.75 0 0 0 6.75 6.75h2.018a5.25 5.25 0 0 0 3.712-1.538l1.732-1.732a5.25 5.25 0 0 0 1.538-3.712l.003-2.024a.668.668 0 0 1 .198-.471 1.575 1.575 0 1 0-2.228-2.228 3.818 3.818 0 0 0-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0 1 16.35 15m.002 0h-.002',
  trophy:              'M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0',
  'arrow-trending-up': 'M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941',
  sun:                 'M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z',
  gift:                'M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z',
};

const BADGE_STYLES = {
  sales:       { from: '#1e6ebf', to: '#3b9eff', glow: 'rgba(46,139,240,0.50)'  },
  customer:    { from: '#166534', to: '#22c55e', glow: 'rgba(34,197,94,0.50)'   },
  performance: { from: '#5b21b6', to: '#a78bfa', glow: 'rgba(139,92,246,0.50)'  },
  special:     { from: '#92400e', to: '#fbbf24', glow: 'rgba(245,158,11,0.50)'  },
};

function AchievementBadgeIcon({ icon, category }) {
  const style = BADGE_STYLES[category] || BADGE_STYLES.sales;
  const path  = ACHIEVEMENT_ICON_PATHS[icon];
  return (
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
      style={{
        background: `linear-gradient(135deg, ${style.from} 0%, ${style.to} 100%)`,
        boxShadow: `0 0 18px ${style.glow}, 0 4px 10px rgba(0,0,0,0.30)`,
      }}
    >
      {path ? (
        <svg className="w-7 h-7 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
          <path strokeLinecap="round" strokeLinejoin="round" d={path} />
        </svg>
      ) : (
        <span className="text-[var(--wm-text-hero)] leading-none select-none">{icon}</span>
      )}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ initials, color, size = 'md' }) {
  const sizes = { sm: 'w-7 h-7 text-[var(--wm-text-caption)]', md: 'w-9 h-9 text-[var(--wm-text-ui)]', lg: 'w-12 h-12 text-[var(--wm-text-body)]', xl: 'w-16 h-16 text-[var(--wm-text-cta)]' };
  return (
    <div
      className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
}

// ─── Level Badge ─────────────────────────────────────────────────────────────

function LevelBadge({ level, size = 'sm' }) {
  const textSize = size === 'xs' ? 'text-[var(--wm-text-micro)]' : 'text-[var(--wm-text-caption)]';
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium ${textSize} text-white flex-shrink-0`}
      style={{ backgroundColor: level.color }}
    >
      Lv{level.level} {level.title}
    </span>
  );
}

// ─── Category Badge ───────────────────────────────────────────────────────────

function CategoryBadge({ category }) {
  const colors = CATEGORY_COLORS[category] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[var(--wm-text-micro)] font-medium capitalize ${colors.bg} ${colors.text}`}>
      {category}
    </span>
  );
}

// ─── XP Progress Bar ─────────────────────────────────────────────────────────

function XPBar({ progressPct, color }) {
  return (
    <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-[#243348] overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${progressPct}%`, backgroundColor: color }}
      />
    </div>
  );
}

// ─── Award XP Modal ───────────────────────────────────────────────────────────

function AwardXPModal({ onClose, prefilledEmployeeId }) {
  const { employees, achievements, awardXP } = useGamification();
  const { addLog } = useAuditLog();
  const { currentRole } = useRoles();
  const [employeeId, setEmployeeId] = useState(prefilledEmployeeId || '');
  const [achievementId, setAchievementId] = useState('');
  const [xpAmount, setXpAmount] = useState('');
  const [note, setNote] = useState('');

  const selectedAchievement = achievements.find(a => a.id === achievementId);
  const isManual = selectedAchievement?.variableXP;
  const previewXP = isManual ? (parseInt(xpAmount, 10) || 0) : (selectedAchievement?.xp || 0);

  const grouped = useMemo(() => {
    const groups = {};
    achievements.forEach(a => {
      if (!groups[a.category]) groups[a.category] = [];
      groups[a.category].push(a);
    });
    return groups;
  }, [achievements]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!employeeId || !achievementId) return;
    if (isManual && (!xpAmount || parseInt(xpAmount, 10) <= 0)) return;
    const recipient = employees.find(emp => emp.id === employeeId);
    awardXP({
      employeeId,
      achievementId,
      xp: previewXP,
      note,
      awardedBy: 'owner',
    });
    addLog('SYSTEM', 'XP_AWARDED', {
      severity: 'success',
      actor: { role: currentRole, label: ROLES[currentRole]?.label || currentRole },
      target: recipient ? `${recipient.name} (${recipient.role})` : employeeId,
      details: {
        recipient: recipient?.name || employeeId,
        achievement: selectedAchievement?.label || achievementId,
        xpAwarded: previewXP,
        note: note || '—',
        awardedBy: ROLES[currentRole]?.label || currentRole,
      },
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-lg shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-[#243348]">
          <span className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Award XP</span>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Employee */}
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1.5">Employee</label>
            <select
              value={employeeId}
              onChange={e => setEmployeeId(e.target.value)}
              required
              className="w-full h-9 px-3 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]"
            >
              <option value="">Select employee…</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.name} — {e.role}</option>
              ))}
            </select>
          </div>

          {/* Achievement */}
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1.5">Achievement</label>
            <select
              value={achievementId}
              onChange={e => { setAchievementId(e.target.value); setXpAmount(''); }}
              required
              className="w-full h-9 px-3 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]"
            >
              <option value="">Select achievement…</option>
              {Object.entries(grouped).map(([category, items]) => (
                <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                  {items.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.icon} {a.label}{a.variableXP ? '' : ` · ${a.xp} XP`}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* XP Amount */}
          {selectedAchievement && (
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1.5">
                XP Amount {isManual ? '' : '(fixed)'}
              </label>
              {isManual ? (
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={xpAmount}
                  onChange={e => setXpAmount(e.target.value)}
                  required
                  placeholder="Enter XP amount"
                  className="w-full h-9 px-3 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] font-mono focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0]"
                />
              ) : (
                <div className="h-9 px-3 flex items-center rounded border border-gray-100 dark:border-[#243348] bg-gray-50 dark:bg-[#0F1923]/60">
                  <span className="text-[var(--wm-text-body)] font-mono font-medium text-[#2E8BF0]">+{selectedAchievement.xp} XP</span>
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1.5">Note <span className="font-normal text-[#64748B] dark:text-[#7D93AE]">(optional)</span></label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={2}
              placeholder="Add context or details…"
              className="w-full px-3 py-2 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] resize-none focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] placeholder:text-[#64748B] dark:placeholder:text-[#7D93AE]"
            />
          </div>

          {/* Awarded by */}
          <div className="flex items-center gap-2 py-2 px-3 rounded bg-gray-50 dark:bg-[#0F1923]/50 border border-gray-100 dark:border-[#243348]">
            <div className="w-5 h-5 rounded-full bg-[#2E8BF0]/15 flex items-center justify-center flex-shrink-0">
              <span className="text-[var(--wm-text-micro)] font-bold text-[#2E8BF0]">WM</span>
            </div>
            <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Awarded by <span className="font-medium text-[#0F1923] dark:text-[#F8FAFE]">Owner</span></span>
          </div>

          {/* Submit */}
          <Button type="submit" variant="primary" className="w-full h-9 rounded text-[var(--wm-text-ui)] font-medium flex items-center justify-center gap-2">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Award XP
          </Button>
        </form>
      </div>
    </div>
  );
}

// ─── Leaderboard Tab ──────────────────────────────────────────────────────────

function LeaderboardTab({ onAward }) {
  const { getRankedEmployees, events } = useGamification();
  const [period, setPeriod] = useState('alltime');

  const allRanked = getRankedEmployees();

  const weekStart = startOfWeek();
  const monthStart = startOfMonth();

  const rankedByPeriod = useMemo(() => {
    if (period === 'alltime') return allRanked;
    const cutoff = period === 'week' ? weekStart : monthStart;
    return [...allRanked]
      .map(emp => {
        const periodXP = events
          .filter(e => e.employeeId === emp.id && new Date(e.timestamp) >= cutoff)
          .reduce((sum, e) => sum + (e.xp || 0), 0);
        return { ...emp, periodXP };
      })
      .sort((a, b) => b.periodXP - a.periodXP);
  }, [period, allRanked, events, weekStart, monthStart]);

  const getDisplayXP = (emp) => {
    if (period === 'week') return emp.periodXP ?? emp.weekXP;
    if (period === 'month') return emp.periodXP ?? emp.monthXP;
    return emp.totalXP;
  };

  const top3 = rankedByPeriod.slice(0, 3);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;

  const podiumHeight = ['h-28', 'h-36', 'h-24'];
  const podiumRing = ['ring-2 ring-gray-300', 'ring-2 ring-amber-400', 'ring-2 ring-amber-700/50'];
  const medalColors = ['#C0C0C0', '#F59E0B', '#CD7F32'];
  const medalLabels = ['2nd', '1st', '3rd'];
  const avatarSize = ['md', 'xl', 'md'];

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-[#1B2A3E] rounded-lg w-fit">
        {[
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'alltime', label: 'All Time' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setPeriod(key)}
            className={`px-3 py-1.5 rounded text-[var(--wm-text-ui)] font-medium transition-colors ${
              period === key
                ? 'bg-white dark:bg-[#243348] text-[#0F1923] dark:text-[#F8FAFE] shadow-sm'
                : 'text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Podium */}
      {top3.length > 0 && (
        <div className="flex items-end justify-center gap-4 pt-4">
          {podiumOrder.map((emp, idx) => {
            if (!emp) return <div key={idx} className="w-28" />;
            const displayXP = getDisplayXP(emp);
            return (
              <div key={emp.id} className="flex flex-col items-center gap-2 w-28">
                {/* Crown for 1st */}
                {idx === 1 && (
                  <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 19.5h19.5M2.25 4.5l4.5 9 5.25-9 5.25 9 4.5-9" /></svg>
                )}
                {/* Avatar with ring */}
                <div className={`rounded-full ${podiumRing[idx]}`}>
                  <Avatar
                    initials={emp.initials}
                    color={emp.color}
                    size={avatarSize[idx]}
                  />
                </div>
                <div className="text-center">
                  <div className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate w-28 text-center">{emp.name}</div>
                  <div className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] truncate">{emp.role}</div>
                  <div className="text-[var(--wm-text-body)] font-bold font-mono mt-0.5" style={{ color: medalColors[idx] }}>
                    {displayXP.toLocaleString()} XP
                  </div>
                  <LevelBadge level={emp.level} size="xs" />
                </div>
                {/* Podium block */}
                <div
                  className={`w-full rounded-t-sm flex items-center justify-center ${podiumHeight[idx]}`}
                  style={{ backgroundColor: `${medalColors[idx]}22`, border: `1px solid ${medalColors[idx]}44` }}
                >
                  <span className="text-[var(--wm-text-heading)] font-bold" style={{ color: medalColors[idx] }}>{medalLabels[idx]}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full rankings table */}
      <div className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-[#243348]">
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] w-10">Rank</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">Employee</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden sm:table-cell">Level</th>
              <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] w-32 hidden md:table-cell">Progress</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden lg:table-cell">Week</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden lg:table-cell">Month</th>
              <th className="text-right px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">All-Time</th>
              <th className="px-4 py-2.5 w-10" />
            </tr>
          </thead>
          <tbody>
            {rankedByPeriod.map((emp, idx) => {
              const rankColors = ['#F59E0B', '#94A3B8', '#CD7F32'];
              const isTopThree = idx < 3;
              return (
                <tr
                  key={emp.id}
                  className={`border-b border-gray-100 dark:border-[#243348]/60 last:border-0 transition-colors hover:bg-gray-50 dark:hover:bg-[#243348] ${idx % 2 === 1 ? 'bg-gray-50/30 dark:bg-[#243348]/20' : ''}`}
                >
                  {/* Rank */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {isTopThree ? (
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${rankColors[idx]}22` }}>
                          <span className="text-[var(--wm-text-caption)] font-bold" style={{ color: rankColors[idx] }}>{idx + 1}</span>
                        </div>
                      ) : (
                        <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] font-mono w-5 text-center">{idx + 1}</span>
                      )}
                    </div>
                  </td>
                  {/* Employee */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar initials={emp.initials} color={emp.color} size="sm" />
                      <div className="min-w-0">
                        <div className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{emp.name}</div>
                        <div className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] truncate">{emp.role}</div>
                      </div>
                    </div>
                  </td>
                  {/* Level */}
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <LevelBadge level={emp.level} size="xs" />
                  </td>
                  {/* Progress bar */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="space-y-1">
                      <XPBar progressPct={emp.progressPct} color={emp.level.color} />
                      <div className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE]">
                        {emp.nextLevel ? `${emp.xpToNext.toLocaleString()} to ${emp.nextLevel.title}` : 'Max level'}
                      </div>
                    </div>
                  </td>
                  {/* Week XP */}
                  <td className="px-4 py-3 text-right hidden lg:table-cell">
                    <span className="text-[var(--wm-text-body)] font-mono text-[#64748B] dark:text-[#7D93AE]">{emp.weekXP.toLocaleString()}</span>
                  </td>
                  {/* Month XP */}
                  <td className="px-4 py-3 text-right hidden lg:table-cell">
                    <span className="text-[var(--wm-text-body)] font-mono text-[#64748B] dark:text-[#7D93AE]">{emp.monthXP.toLocaleString()}</span>
                  </td>
                  {/* All-time XP */}
                  <td className="px-4 py-3 text-right">
                    <span className="text-[var(--wm-text-body)] font-mono font-medium text-[#2E8BF0]">{emp.totalXP.toLocaleString()}</span>
                  </td>
                  {/* Award button — hidden when onAward is null (no permission) */}
                  <td className="px-4 py-3">
                    {onAward ? (
                      <button
                        onClick={() => onAward(emp.id)}
                        title="Award XP"
                        className="w-6 h-6 flex items-center justify-center rounded bg-[#2E8BF0]/10 text-[#2E8BF0] hover:bg-[#2E8BF0]/20 transition-colors"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                      </button>
                    ) : (
                      <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                      </svg>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rankedByPeriod.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No employees found.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── History Tab ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

function HistoryTab() {
  const { events, employees, achievements } = useGamification();
  const [empFilter, setEmpFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [page, setPage] = useState(0);

  const categories = useMemo(() => [...new Set(achievements.map(a => a.category))], [achievements]);

  const filtered = useMemo(() => {
    return events.filter(e => {
      if (empFilter !== 'all' && e.employeeId !== empFilter) return false;
      if (catFilter !== 'all') {
        const ach = achievements.find(a => a.id === e.achievementId);
        if (!ach || ach.category !== catFilter) return false;
      }
      return true;
    });
  }, [events, empFilter, catFilter, achievements]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleFilterChange = () => setPage(0);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={empFilter}
          onChange={e => { setEmpFilter(e.target.value); handleFilterChange(); }}
          className="h-8 px-2.5 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0]"
        >
          <option value="all">All Employees</option>
          {employees.map(e => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        <select
          value={catFilter}
          onChange={e => { setCatFilter(e.target.value); handleFilterChange(); }}
          className="h-8 px-2.5 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0]"
        >
          <option value="all">All Categories</option>
          {categories.map(c => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
        <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] ml-auto">{filtered.length} event{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-lg overflow-hidden">
        {paged.length > 0 ? (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-[#243348]">
                  <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">Date & Time</th>
                  <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">Employee</th>
                  <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden sm:table-cell">Achievement</th>
                  <th className="text-right px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE]">XP</th>
                  <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden md:table-cell">Awarded By</th>
                  <th className="text-left px-4 py-2.5 text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-[#64748B] dark:text-[#7D93AE] hidden lg:table-cell">Note</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((event, idx) => {
                  const emp = employees.find(e => e.id === event.employeeId);
                  const ach = achievements.find(a => a.id === event.achievementId);
                  return (
                    <tr
                      key={event.id}
                      className={`border-b border-gray-100 dark:border-[#243348]/60 last:border-0 hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30 dark:bg-[#243348]/20' : ''}`}
                    >
                      <td className="px-4 py-3 text-[var(--wm-text-body)] font-mono text-[#64748B] dark:text-[#7D93AE] whitespace-nowrap">
                        <div>{formatDate(event.timestamp)}</div>
                        <div className="text-[var(--wm-text-caption)]">{formatTime(event.timestamp)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {emp ? (
                          <div className="flex items-center gap-2">
                            <Avatar initials={emp.initials} color={emp.color} size="sm" />
                            <div className="min-w-0">
                              <div className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">{emp.name}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {ach ? (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[var(--wm-text-body)]">{ach.icon}</span>
                            <span className="text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE]">{ach.label}</span>
                            <CategoryBadge category={ach.category} />
                          </div>
                        ) : (
                          <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-[var(--wm-text-body)] font-mono font-medium text-[#2E8BF0]">+{event.xp}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE] capitalize">{event.awardedBy}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">{event.note || '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-[#243348]">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="h-7 px-3 rounded border border-gray-200 dark:border-[#243348] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Prev
                </button>
                <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="h-7 px-3 rounded border border-gray-200 dark:border-[#243348] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="py-16 flex flex-col items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-[#243348] flex items-center justify-center">
              <svg className="w-6 h-6 text-[#64748B] dark:text-[#7D93AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No events yet</p>
            <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">Award XP to start tracking performance.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Achievements Tab ─────────────────────────────────────────────────────────

function AchievementsTab() {
  const { achievements, events } = useGamification();

  const timesEarned = useMemo(() => {
    const counts = {};
    events.forEach(e => {
      counts[e.achievementId] = (counts[e.achievementId] || 0) + 1;
    });
    return counts;
  }, [events]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {achievements.map(ach => {
        const count = timesEarned[ach.id] || 0;
        return (
          <div
            key={ach.id}
            className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-xl p-4 flex gap-4 items-start hover:border-[#2E8BF0]/40 dark:hover:border-[#2E8BF0]/40 hover:shadow-md dark:hover:shadow-[#0F1923]/60 transition-all"
          >
            <AchievementBadgeIcon icon={ach.icon} category={ach.category} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="font-semibold text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] leading-tight">{ach.label}</div>
                <div className="font-mono font-bold text-[var(--wm-text-body)] text-[#2E8BF0] flex-shrink-0">
                  {ach.variableXP ? 'Variable' : `+${ach.xp} XP`}
                </div>
              </div>
              <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] leading-relaxed mb-2">{ach.description}</p>
              <div className="flex items-center gap-2">
                <CategoryBadge category={ach.category} />
                <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">
                  Awarded {count === 0 ? 'never' : `${count}×`}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

function TeamTab() {
  const { employees, addEmployee, removeEmployee, getEmployeeStats } = useGamification();
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('');
  const [newColor, setNewColor] = useState(AVATAR_PALETTE[0]);
  const [confirmRemove, setConfirmRemove] = useState(null);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newName.trim() || !newRole.trim()) return;
    const initials = newName.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    addEmployee({ name: newName.trim(), initials, role: newRole.trim(), color: newColor });
    setNewName('');
    setNewRole('');
    setNewColor(AVATAR_PALETTE[0]);
    setShowForm(false);
  };

  return (
    <div className="space-y-4">
      {/* Add form toggle */}
      <div className="flex items-center justify-between">
        <span className="text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE]">{employees.length} team member{employees.length !== 1 ? 's' : ''}</span>
        <Button variant="primary" size="sm" className="flex items-center gap-1.5" onClick={() => setShowForm(s => !s)}>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Add Employee
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white dark:bg-[#1B2A3E] border border-[#2E8BF0]/40 rounded-lg p-4">
          <h3 className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] mb-3">New Team Member</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1">Full Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  required
                  placeholder="e.g. Alex T."
                  className="w-full h-8 px-2.5 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] placeholder:text-[#64748B]"
                />
              </div>
              <div>
                <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1">Role</label>
                <input
                  type="text"
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                  required
                  placeholder="e.g. Installer"
                  className="w-full h-8 px-2.5 rounded border border-gray-200 dark:border-[#243348] bg-[#F8FAFE] dark:bg-[#0F1923] text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] placeholder:text-[#64748B]"
                />
              </div>
            </div>
            <div>
              <label className="block text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] mb-1.5">Avatar Color</label>
              <div className="flex items-center gap-2">
                {AVATAR_PALETTE.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNewColor(c)}
                    className={`w-6 h-6 rounded-full transition-all ${newColor === c ? 'ring-2 ring-offset-2 ring-[#2E8BF0] dark:ring-offset-[#1B2A3E]' : ''}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                <div className="ml-2 w-8 h-8 rounded-full flex items-center justify-center text-white text-[var(--wm-text-ui)] font-semibold" style={{ backgroundColor: newColor }}>
                  {newName ? newName.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : 'AB'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button type="submit" variant="primary" size="sm">
                Add Member
              </Button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="h-8 px-4 rounded border border-gray-200 dark:border-[#243348] text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Employee cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {employees.map(emp => {
          const stats = getEmployeeStats(emp.id);
          return (
            <div key={emp.id} className="bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <Avatar initials={emp.initials} color={emp.color} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate">{emp.name}</div>
                  <div className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] truncate">{emp.role}</div>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[var(--wm-text-heading)] font-mono font-bold text-[#2E8BF0]">{stats.totalXP.toLocaleString()}</div>
                  <div className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">Total XP</div>
                </div>
                <LevelBadge level={stats.level} />
              </div>
              <XPBar progressPct={stats.progressPct} color={stats.level.color} />
              {/* Remove */}
              {confirmRemove === emp.id ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] flex-1">Remove {emp.name}?</span>
                  <button
                    onClick={() => { removeEmployee(emp.id); setConfirmRemove(null); }}
                    className="h-6 px-2 rounded bg-[#EF4444]/10 text-[#EF4444] text-[var(--wm-text-caption)] font-medium hover:bg-[#EF4444]/20 transition-colors"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmRemove(null)}
                    className="h-6 px-2 rounded border border-gray-200 dark:border-[#243348] text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmRemove(emp.id)}
                  className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] hover:text-[#EF4444] transition-colors self-start"
                >
                  Remove
                </button>
              )}
            </div>
          );
        })}
      </div>

      {employees.length === 0 && (
        <div className="py-12 text-center text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No team members. Add one above.</div>
      )}
    </div>
  );
}

// ─── Performance Page ─────────────────────────────────────────────────────────

export default function PerformancePage({ can = () => true }) {
  const [tab, setTab] = useState('leaderboard');
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [prefilledEmployeeId, setPrefilledEmployeeId] = useState('');

  const canAwardXP     = can('performance.award_xp');
  const canManageTeam  = can('performance.manage_team');
  const canViewAll     = can('performance.view.all');

  // Hide "Team" tab if user can't manage team; hide award button if no permission
  const tabs = [
    { key: 'leaderboard', label: 'Leaderboard' },
    { key: 'history',     label: 'History'     },
    { key: 'achievements',label: 'Achievements' },
    ...(canManageTeam ? [{ key: 'team', label: 'Team' }] : []),
  ];

  const handleAward = (employeeId) => {
    if (!canAwardXP) return;
    setPrefilledEmployeeId(employeeId || '');
    setAwardModalOpen(true);
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-auto bg-[#F8FAFE] dark:bg-[#0F1923]">
      {/* Page header */}
      <div className="h-12 flex-shrink-0 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-[#2E8BF0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
          <span className="text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">Performance &amp; Rewards</span>
          {!canViewAll && (
            <span className="px-1.5 py-0.5 rounded text-[var(--wm-text-micro)] font-semibold uppercase tracking-wide bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              Own records only
            </span>
          )}
        </div>
        {canAwardXP && (
          <Button variant="primary" size="sm" className="flex items-center gap-1.5" onClick={() => handleAward('')}>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Award XP
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] px-6">
        <div className="flex gap-0">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-[var(--wm-text-ui)] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.key
                  ? 'text-[#2E8BF0] border-[#2E8BF0]'
                  : 'text-[#64748B] dark:text-[#7D93AE] border-transparent hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-gray-200 dark:hover:border-[#243348]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 p-6 overflow-auto">
        {tab === 'leaderboard'  && <LeaderboardTab onAward={canAwardXP ? handleAward : null} />}
        {tab === 'history'      && <HistoryTab />}
        {tab === 'achievements' && <AchievementsTab />}
        {tab === 'team'         && canManageTeam && <TeamTab />}
      </div>

      {/* Award XP modal */}
      {awardModalOpen && (
        <AwardXPModal
          onClose={() => { setAwardModalOpen(false); setPrefilledEmployeeId(''); }}
          prefilledEmployeeId={prefilledEmployeeId}
        />
      )}
    </div>
  );
}
